import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { mentorshipApi, showError, showSuccess } from "../lib";
import { useAuth } from "../context/AuthContext";
import {
  Calendar,
  Clock,
  Video,
  XCircle,
  Loader,
  Star,
  User,
  Briefcase,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  X,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
} from "lucide-react";

interface Session {
  _id: string;
  mentor: {
    _id: string;
    user?: {
      name: string;
      firstName?: string;
      lastName?: string;
      avatar?: string;
    };
    specialty?: string;
    company?: string;
    expertise?: string[];
    rating?: number;
    reviewsCount?: number;
    avatar?: string;
  };
  student: {
    _id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  scheduledAt: string;
  duration: number;
  status: "pending" | "approved" | "confirmed" | "completed" | "cancelled" | "no-show";
  price: number;
  topic: string;
  notes?: string;
  meetingLink?: string;
  createdAt?: string;
}

const StudentMentorshipSessionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState<
    "" | "date-asc" | "date-desc" | "price-asc" | "price-desc" | "status" | "applied-desc" | "applied-asc"
  >("applied-desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchSessions = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const response = await mentorshipApi.getMySessions({
        role: "student",
        status: statusFilter || undefined,
        limit: 100,
      });
      setSessions(response.data || []);
      setTotalItems(response.data?.length || 0);
      setTotalPages(Math.ceil((response.data?.length || 0) / itemsPerPage));
      setCurrentPage(1);
    } catch (err) {
      showError("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  // Compute status counts
  const statusCounts = useMemo(() => {
    const counts = {
      all: sessions.length,
      pending: 0,
      approved: 0,
      confirmed: 0,
      completed: 0,
      cancelled: 0,
      "no-show": 0,
    };
    sessions.forEach((s) => {
      if (s.status in counts) {
        counts[s.status as keyof typeof counts]++;
      }
    });
    return counts;
  }, [sessions]);

  const handleCancelSession = async (sessionId: string) => {
    if (!confirm("Cancel this session?")) return;
    try {
      const res = await mentorshipApi.cancelSession(sessionId);
      if (res.success) {
        showSuccess("Session cancelled");
        fetchSessions();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to cancel");
    }
  };

  const handlePayForSession = async (sessionId: string) => {
    try {
      const res = await mentorshipApi.createMentorshipPayment(sessionId);
      if (res.success) {
        const payUrl = res.data?.paymentLink || res.data?.paymentUrl;
        if (payUrl) {
          window.location.href = payUrl;
        } else {
          showError("Payment link not generated");
        }
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to initiate payment");
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { bg: string; text: string; label: string }> =
      {
        pending: {
          bg: "bg-amber-100",
          text: "text-amber-700",
          label: "Pending Approval",
        },
        approved: {
          bg: "bg-emerald-100",
          text: "text-emerald-700",
          label: "Approved - Pay Now",
        },
        confirmed: {
          bg: "bg-emerald-100",
          text: "text-emerald-700",
          label: "Confirmed",
        },
        completed: {
          bg: "bg-blue-100",
          text: "text-blue-700",
          label: "Completed",
        },
        cancelled: {
          bg: "bg-red-100",
          text: "text-red-700",
          label: "Cancelled",
        },
        "no-show": {
          bg: "bg-gray-100",
          text: "text-gray-700",
          label: "No-Show",
        },
      };
    return configs[status] || configs.pending;
  };

  const fmtXAF = (amount: number) =>
    new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: "XAF",
      maximumFractionDigits: 0,
    }).format(amount);

  const getMentorInfo = (session: Session) => {
    const mentor = session.mentor;
    if (!mentor)
      return {
        name: "Pending Mentor",
        avatar: null,
        subtitle: "Awaiting confirmation",
        rating: undefined,
      };
    const m = mentor.user || mentor;
    const name =
      m.name ||
      m.firstName ||
      m.lastName
        ? `${m.firstName || ""} ${m.lastName || ""}`.trim() || "Mentor"
        : "Mentor";
    return {
      name,
      avatar: m.avatar,
      subtitle: mentor.specialty || mentor.company || "",
      rating: mentor.rating,
    };
  };

  const filteredAndSortedSessions = useMemo(() => {
    let result = [...sessions];

    if (statusFilter) {
      result = result.filter((s) => s.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((s) => {
        const mentorName = getMentorInfo(s).name.toLowerCase();
        const studentName =
          s.student?.name?.toLowerCase() ||
          `${s.student?.firstName || ""} ${s.student?.lastName || ""}`
            .toLowerCase()
            .trim();
        const topic = (s.topic || "").toLowerCase();
        return (
          mentorName.includes(query) ||
          studentName.includes(query) ||
          topic.includes(query)
        );
      });
    }

    if (dateFilter.start || dateFilter.end) {
      result = result.filter((s) => {
        const sessionDate = new Date(s.scheduledAt).getTime();
        if (
          dateFilter.start &&
          sessionDate < new Date(dateFilter.start).setHours(0, 0, 0, 0)
        ) {
          return false;
        }
        if (
          dateFilter.end &&
          sessionDate > new Date(dateFilter.end).setHours(23, 59, 59, 999)
        ) {
          return false;
        }
        return true;
      });
    }

    if (sortBy) {
      result.sort((a, b) => {
        switch (sortBy) {
          case "date-asc":
            return (
              new Date(a.scheduledAt).getTime() -
              new Date(b.scheduledAt).getTime()
            );
          case "date-desc":
            return (
              new Date(b.scheduledAt).getTime() -
              new Date(a.scheduledAt).getTime()
            );
          case "price-asc":
            return a.price - b.price;
          case "price-desc":
            return b.price - a.price;
          case "status": {
            const statusOrder = {
              pending: 0,
              approved: 1,
              confirmed: 2,
              completed: 3,
              cancelled: 4,
              "no-show": 5,
            };
            return (statusOrder[a.status] || 6) - (statusOrder[b.status] || 6);
          }
          case "applied-asc":
            return (
              new Date(a.createdAt || a.scheduledAt).getTime() -
              new Date(b.createdAt || b.scheduledAt).getTime()
            );
          case "applied-desc":
            return (
              new Date(b.createdAt || b.scheduledAt).getTime() -
              new Date(a.createdAt || a.scheduledAt).getTime()
            );
          default:
            return 0;
        }
      });
    }

    setTotalItems(result.length);
    setTotalPages(Math.ceil(result.length / itemsPerPage));

    return result;
  }, [sessions, statusFilter, searchQuery, dateFilter, sortBy, itemsPerPage]);

  const currentSessions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedSessions.slice(startIndex, endIndex);
  }, [filteredAndSortedSessions, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, dateFilter, sortBy]);

  useEffect(() => {
    fetchSessions();
  }, [user]);

  const activeFilterCount =
    (statusFilter ? 1 : 0) +
    (searchQuery.trim() ? 1 : 0) +
    (dateFilter.start || dateFilter.end ? 1 : 0) +
    (sortBy ? 1 : 0);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen pt-16 bg-gray-50">
        <Loader className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen pt-12 pb-12 bg-gray-50">
      <div className="max-w-2xl px-3 mx-auto">
        {/* Header */}
        <div className="py-4">
          <h1 className="text-xl font-bold text-gray-900">My Sessions</h1>
          <p className="text-xs text-gray-500">Your mentorship sessions</p>
        </div>

        {/* Search and Controls */}
        <div className="mb-4 space-y-3">
          {/* Search & Quick Filters */}
          <div className="flex flex-wrap items-stretch gap-2">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sessions..."
                className="w-full py-2 pr-4 text-xs transition-colors bg-white border border-gray-200 rounded-lg pl-9 focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>

            <button
              onClick={() => {
                setShowDateFilter(!showDateFilter);
                setShowSortDropdown(false);
              }}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                dateFilter.start || dateFilter.end
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              <span>Date</span>
              {(dateFilter.start || dateFilter.end) && (
                <span className="ml-0.5 w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center">
                  •
                </span>
              )}
            </button>

            <div className="relative">
              <button
                onClick={() => {
                  setShowSortDropdown(!showSortDropdown);
                  setShowDateFilter(false);
                }}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                  sortBy
                    ? "bg-gray-900 text-white border border-gray-900"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
                }`}
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
                <span>
                  {sortBy
                    ? sortBy === "date-asc"
                      ? "Date ↑"
                      : sortBy === "date-desc"
                        ? "Date ↓"
                        : sortBy === "price-asc"
                          ? "Price ↑"
                          : sortBy === "price-desc"
                            ? "Price ↓"
                            : "Status"
                    : "Sort"}
                </span>
                {showSortDropdown ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>
              {showSortDropdown && (
                <div className="absolute top-full left-0 mt-1 py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[180px]">
                  {[
                    { value: "", label: "Clear sort" },
                    { value: "applied-desc", label: "Applied: New → Old" },
                    { value: "applied-asc", label: "Applied: Old → New" },
                    { value: "date-desc", label: "Date: New → Old" },
                    { value: "date-asc", label: "Date: Old → New" },
                    { value: "price-asc", label: "Price: Low → High" },
                    { value: "price-desc", label: "Price: High → Low" },
                    { value: "status", label: "By Status" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSortBy(opt.value as any);
                        setShowSortDropdown(false);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 ${
                        sortBy === opt.value
                          ? "bg-gray-50 text-gray-900 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setStatusFilter("");
                  setSearchQuery("");
                  setDateFilter({ start: "", end: "" });
                  setSortBy("");
                  setShowDateFilter(false);
                  setShowSortDropdown(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 transition-colors bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200"
              >
                Clear all ({activeFilterCount})
              </button>
            )}
          </div>

          {/* Date Range Picker */}
          {showDateFilter && (
            <div className="flex flex-wrap items-end gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex-1 min-w-[140px]">
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateFilter.start}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, start: e.target.value })
                  }
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateFilter.end}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, end: e.target.value })
                  }
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                />
              </div>
              {(dateFilter.start || dateFilter.end) && (
                <button
                  onClick={() => setDateFilter({ start: "", end: "" })}
                  className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg border border-red-100"
                >
                  Clear dates
                </button>
              )}
            </div>
          )}

          {/* Status Pills - FIXED: Using statusCounts */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: "", label: "All", count: statusCounts.all },
              { key: "pending", label: "Pending", count: statusCounts.pending },
              { key: "approved", label: "Approved", count: statusCounts.approved },
              { key: "confirmed", label: "Confirmed", count: statusCounts.confirmed },
              { key: "completed", label: "Completed", count: statusCounts.completed },
              { key: "cancelled", label: "Cancelled", count: statusCounts.cancelled },
            ].map((item) => (
              <button
                key={item.key || "all"}
                onClick={() => setStatusFilter(item.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  statusFilter === item.key
                    ? "bg-gray-900 text-white shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
                }`}
              >
                {item.label}
                <span
                  className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                    statusFilter === item.key ? "bg-white/20" : "bg-gray-100"
                  }`}
                >
                  {item.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Sessions List */}
        {currentSessions.length === 0 ? (
          <div className="py-12 text-center bg-white border border-gray-200 rounded-xl">
            <Calendar className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            <p className="mb-4 text-sm text-gray-500">No sessions found</p>
            <button
              onClick={() => navigate("/mentorship")}
              className="px-4 py-2 text-xs font-medium text-white transition-colors bg-orange-500 rounded-lg hover:bg-orange-600"
            >
              Find Mentor
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {currentSessions.map((session) => {
                const mentor = getMentorInfo(session);
                const { date, time } = formatDateTime(session.scheduledAt);
                const statusConfig = getStatusConfig(session.status);

                return (
                  <div
                    key={session._id}
                    className="p-4 transition-shadow bg-white border border-gray-200 shadow-sm rounded-xl hover:shadow-md"
                  >
                    {/* Mentor Info */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-full shrink-0">
                        {mentor.avatar ? (
                          <img
                            src={mentor.avatar}
                            className="object-cover w-10 h-10 rounded-full"
                            alt=""
                          />
                        ) : (
                          <User className="w-5 h-5 text-orange-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h6 className="text-sm font-semibold text-gray-900 truncate">
                          {mentor.name}
                        </h6>
                        <p className="text-xs text-gray-500 truncate">
                          {session.topic || "Mentorship Session"}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
                      >
                        {statusConfig.label}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{time}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        <span>{fmtXAF(session.price)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {session.status === "approved" && session.price > 0 && (
                        <button
                          onClick={() => handlePayForSession(session._id)}
                          className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-emerald-700 transition-colors"
                        >
                          <Briefcase className="w-3 h-3" /> Pay Now ({fmtXAF(session.price)})
                        </button>
                      )}

                      {session.status === "pending" && (
                        <span className="px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg flex items-center gap-1.5">
                          <Clock className="w-3 h-3" /> Waiting for mentor approval
                        </span>
                      )}

                      {(session.status === "approved" || session.status === "confirmed") && (
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/student/messages?user=${
                                (session.mentor as any).user?._id || session.mentor._id
                              }`,
                            )
                          }
                          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-blue-700 transition-colors"
                        >
                          <MessageCircle className="w-3 h-3" /> Message
                        </button>
                      )}

                      {session.status === "confirmed" && (
                        <>
                          <button
                            onClick={() => navigate(`/video-room/${session._id}`)}
                            className="px-3 py-1.5 bg-orange-500 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-orange-600 transition-colors"
                          >
                            <Video className="w-3 h-3" /> Join session
                          </button>
                          {session.meetingLink && (
                            <a
                              href={session.meetingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-gray-200 transition-colors"
                            >
                              <Video className="w-3 h-3" /> Open Zoom
                            </a>
                          )}
                        </>
                      )}

                      {session.status === "completed" && (
                        <button
                          onClick={() =>
                            navigate(`/mentorship/sessions/${session._id}/review`)
                          }
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-gray-200 transition-colors"
                        >
                          <Star className="w-3 h-3" /> Write a Review
                        </button>
                      )}

                      {(session.status === "pending" || session.status === "approved") && (
                        <button
                          onClick={() => handleCancelSession(session._id)}
                          className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-red-100 transition-colors"
                        >
                          <XCircle className="w-3 h-3" /> Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center gap-3 mt-6">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>Show:</span>
                  {[5, 10, 20, 50].map((value) => (
                    <button
                      key={value}
                      onClick={() => handleItemsPerPageChange(value)}
                      className={`px-2 py-1 rounded ${
                        itemsPerPage === value
                          ? "bg-orange-500 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg border transition-colors ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                        : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"
                    }`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? "bg-orange-500 text-white"
                              : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg border transition-colors ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                        : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"
                    }`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="text-xs text-gray-400">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} sessions
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default StudentMentorshipSessionsPage;